import logger from '@/lib/logger';
import { useNavigate } from 'react-router-dom';
import { toRawImpedanceResult } from '@/lib/impedance';
import { useEffect, useState, useRef } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, Alert } from '@mui/material';
import { VideoPlayer } from '../VideoPlayer';
import { BaselineReviewOverlay } from '../BaselineReviewOverlay';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { useDeviceStore } from '@/store/useDeviceStore';
import { useUser } from '@/store/useAuthStore';
import { features } from '@/config/features';
import { useDeviceService } from '@/contexts/DeviceServiceContext';
import { useDeviceConnection } from '@/hooks/useDevice';
import { MeasurementInterruptedModal } from '../MeasurementInterruptedModal';
import { DeviceReconnectDialog } from '../DeviceReconnectDialog';
import { buildDeviceStatePayload, saveMeasurementWithFallback } from '../../utils/measurementPersistence';
import { testService } from '@/services/api/testService';
import { usePhaseDisconnectRecovery } from '../../hooks/usePhaseDisconnectRecovery';
/**
 * Step 3: Take Specimen (Baseline Measurement)
 * 
 * CRITICAL: Auto-starts baseline measurement in background when step loads.
 * User is NOT aware of this - it runs silently while they collect specimen.
 * Next button disabled until baseline completes (~20s).
 */
export function Step3TakeSpecimen() {
  const nextStep = useMeasurementWizardStore((s) => s.setCurrentStep);
  const setBaselineProgress = useMeasurementWizardStore((s) => s.setBaselineProgress);
  const completeBaseline = useMeasurementWizardStore((s) => s.setBaselineCompletedAt);
  const setBaselineResult = useMeasurementWizardStore((s) => s.setBaselineResult);
  const testId = useMeasurementWizardStore((s) => s.testId);
  const sampleCode = useMeasurementWizardStore((s) => s.sampleCode);
  const baselineResult = useMeasurementWizardStore((s) => s.baselineResult);
  const baselineCompletedAt = useMeasurementWizardStore((s) => s.baselineCompletedAt);
  const selectedProfile = useMeasurementWizardStore((s) => s.selectedProfile);
  const initialDeviceSnapshot = useMeasurementWizardStore((s) => s.initialDeviceSnapshot);
  const setMeasurementInFlight = useMeasurementWizardStore((s) => s.setMeasurementInFlight);
  const clearMeasurementInFlight = useMeasurementWizardStore((s) => s.clearMeasurementInFlight);
  const deviceUid = useDeviceStore((s) => s.deviceInfo?.uid ?? '');
  const user = useUser();
  const deviceService = useDeviceService();
  const { isConnected } = useDeviceConnection();
  const [isBaselineRunning, setIsBaselineRunning] = useState(true);
  const navigate = useNavigate();
  const [showRefreshRecovery, setShowRefreshRecovery] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [blockingError, setBlockingError] = useState<{
    title: string;
    message: string;
    canRetry: boolean;
  } | null>(null);
  const [_timeElapsed, setTimeElapsed] = useState(0); // Track time spent on Step 3
  const baselineStartedRef = useRef(false);
  const isMountedRef = useRef(true);
  const resumeStartedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Track time spent on Step 3 - need 60s before button enables
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Subscribe to a baseline measurement already running on the device.
  // Called on mount (page refresh recovery) and after disconnect+reconnect Case B.
  const subscribeToRunningBaseline = async () => {
    try {
      const completionMsg = await deviceService.subscribeMeasurement({
        onProgress: (progress) => {
          if (isMountedRef.current) {
            setBaselineProgress(progress);
          }
        },
      });

      const deviceInfo = useDeviceStore.getState().deviceInfo;
      const { testId: currentTestId, initialDeviceSnapshot: currentSnapshot } =
        useMeasurementWizardStore.getState();
      const nowDate = new Date();

      // Store result in wizard — same shape Step3 normally stores
      const fakeResult = {
        deviceUid: deviceInfo?.uid ?? '',
        deviceSerialNumber: deviceInfo?.serialNumber ?? 0,
        firmwareVersion: deviceInfo?.firmwareVersion ?? '',
        hardwareVersion: deviceInfo?.hardwareVersion ?? '',
        durationMs: 0,
        impedanceResult: completionMsg.impedanceResult,
        completedAt: nowDate,
      };
      setBaselineResult(fakeResult as any);
      completeBaseline(nowDate);
      // Mirror the normal baseline-completion path: transition to 'baseline-complete'
      // so that NewMeasurement's hasPendingRecovery guard remains true on the next
      // page reload. Calling clearMeasurementInFlight() here left measurementInFlight
      // null, causing the reload-to-Dashboard bug at Steps 4–5 when the device
      // wasn't immediately available.
      setMeasurementInFlight({ phase: 'baseline-complete', deviceUid: deviceInfo?.uid ?? '' });

      if (isMountedRef.current) {
        setIsBaselineRunning(false);
      }

      if (currentTestId && completionMsg.impedanceResult) {
        const baselinePayload = {
          impedanceResultJson: toRawImpedanceResult(completionMsg.impedanceResult),
          completedAt: nowDate.toISOString(),
          deviceUid: deviceInfo?.uid ?? '',
          firmwareVersion: deviceInfo?.firmwareVersion ?? '',
          hardwareVersion: deviceInfo?.hardwareVersion ?? '',
          deviceState: buildDeviceStatePayload(currentSnapshot),
        };
        await saveMeasurementWithFallback(currentTestId, 'baseline', baselinePayload).catch((err) => {
          logger.error('[Step3] Failed to save recovered baseline:', err);
        });
      }
    } catch (err) {
      logger.error('[Step3] subscribeMeasurement failed during recovery:', err);
      clearMeasurementInFlight();
      if (isMountedRef.current) {
        setIsBaselineRunning(false);
        setErrorMessage(
          err instanceof Error ? err.message : 'Recovery failed during baseline measurement.',
        );
      }
    }
  };

  // Resume interrupted baseline: if measurementInFlight.phase === 'baseline', subscribe to
  // the already-running measurement instead of starting a new one. This effect must run
  // BEFORE the normal start effect so it can set baselineStartedRef to block runMeasurement().
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const inFlight = useMeasurementWizardStore.getState().measurementInFlight;
    logger.warn('[RECOVERY:Step3] Mount — measurementInFlight:', inFlight, '| resumeStartedRef:', resumeStartedRef.current);
    if (inFlight?.phase !== 'baseline') {
      logger.warn('[RECOVERY:Step3] Not a baseline inFlight — skipping recovery. phase =', inFlight?.phase ?? 'null');
      return;
    }
    if (resumeStartedRef.current) {
      logger.warn('[RECOVERY:Step3] resumeStartedRef already set — skipping (StrictMode double-invoke)');
      return;
    }

    resumeStartedRef.current = true;
    // Block the normal baseline start effect
    baselineStartedRef.current = true;

    logger.warn('[RECOVERY:Step3] Baseline inFlight detected — opening recovery modal');
    setShowRefreshRecovery(true);
  }, []); // Only on mount — deps intentionally omitted; reads from store/refs directly

  useEffect(() => {
    // Prevent duplicate baseline starts caused by React 18 Strict Mode's
    // double-invoke of effects (unmount + remount on the same instance).
    // useRef values persist across the fake Strict Mode remount cycle, so
    // once set to true here the guard remains true on the second invocation.
    // On a genuine navigation-away + back, a new component instance is created
    // and the ref naturally starts as false — no reset effect needed.
    if (baselineStartedRef.current) {
      logger.debug('[Step3] Baseline already started, skipping duplicate start');
      return;
    }
    baselineStartedRef.current = true;

    const startBaseline = async () => {
      // Validate device is connected before starting baseline
      if (!isConnected) {
        setIsBaselineRunning(false);
        setBlockingError({
          title: 'Reader Not Connected',
          message: 'The measurement device is not connected. Please connect the device before starting baseline measurement.',
          canRetry: true,
        });
        return;
      }

      try {
        // Clear any previous errors
        setBlockingError(null);

        // Set mock device to baseline mode if supported
        deviceService.setMeasurementPhase?.('baseline');

        // Validate profile exists - required for consistent measurement
        if (!selectedProfile) {
          throw new Error('No measurement profile selected. Cannot run baseline measurement.');
        }

        setBaselineProgress(0); // Initialize baseline progress

        // Ping device to get fresh state before baseline measurement
        // This eliminates race conditions and gives us authoritative device status
        const freshState = await deviceService.getState();
        const processingId = Number(freshState?.state?.processingId ?? 0n);

        if (processingId !== 0) {
          logger.debug('[Step3] Device is busy with processingId=', processingId, ' - cannot start baseline');
          setIsBaselineRunning(false);
          setBlockingError({
            title: 'Device is Busy',
            message: 'The reader is currently executing a measurement. Please wait for it to finish, or power-cycle the device and reconnect before retrying.',
            canRetry: false,
          });
          return;
        }

        logger.debug('[Step3] Device fresh state confirmed (processingId=0), calling runMeasurement for baseline...');

        // Mark measurement as in-flight for recovery after browser refresh
        setMeasurementInFlight({ phase: 'baseline', deviceUid });

        const result = await deviceService.runMeasurement({
          impedanceExec: selectedProfile.impedanceExecJson,
          onProgress: (progress) => {
            if (isMountedRef.current) {
              setBaselineProgress(progress);
            }
          },
        });

        // CRITICAL: Always update wizard store even if component unmounted
        // User may have navigated to Step 4 while baseline was running
        
        // Store baseline result and completion timestamp in wizard state
        setBaselineResult(result as any); // Full measurement result
        completeBaseline(new Date());

        // Baseline completed — transition to baseline-complete so reload recovery
        // can return the user to Step 4 without needing to re-probe the device.
        setMeasurementInFlight({ phase: 'baseline-complete', deviceUid });
        
        // Only update component state if still mounted
        if (isMountedRef.current) {
          setIsBaselineRunning(false);
        }

        // Save baseline measurement to backend (always, even if component unmounted)
        if (testId) {
          const baselinePayload = {
            impedanceResultJson: toRawImpedanceResult(result.impedanceResult),
            completedAt: result.completedAt.toISOString(),
            deviceUid: result.deviceUid,
            firmwareVersion: result.firmwareVersion,
            hardwareVersion: result.hardwareVersion,
            deviceState: buildDeviceStatePayload(initialDeviceSnapshot),
          };
          await saveMeasurementWithFallback(testId, 'baseline', baselinePayload);
          logger.debug('[Step3] Baseline measurement saved successfully');
        } else {
          logger.warn('No testId available - baseline measurement not saved to backend');
        }
      } catch (error) {
        // If the device is now disconnected, MeasurementInterruptedModal will handle recovery.
        // Do not clear measurementInFlight — the modal needs it for the probe.
        // NOTE: `isConnected` is a stale closure value — read live state from the store.
        // Use isEffectivelyDisconnected() which matches both 'disconnected' and 'error'
        // (DeviceService.handleDisconnect leaves the store in 'error' after setError()).
        if (useDeviceStore.getState().isEffectivelyDisconnected()) {
          logger.debug('[Step3] Baseline threw due to device disconnect — MeasurementInterruptedModal will handle recovery');
          return;
        }

        // Device-side error (device still connected) — clear and report
        clearMeasurementInFlight();

        // DeviceError is a plain interface object (not instanceof Error) — extract message from either shape
        const isDeviceError = (e: unknown): e is { code: number; message: string; recoveryAction?: string } =>
          typeof e === 'object' && e !== null && 'code' in e && 'message' in e;

        const errorMsg: string =
          error instanceof Error
            ? error.message
            : isDeviceError(error)
            ? error.message
            : 'Unknown error';
        const errorCode: number | null = isDeviceError(error) ? error.code : null;
        const recoveryAction: string | null = isDeviceError(error) ? (error.recoveryAction ?? null) : null;

        logger.error('[Step3] Baseline measurement error:', {
          errorMsg,
          errorCode,
          recoveryAction,
          raw: error,
        });

        if (isMountedRef.current) {
          setIsBaselineRunning(false);

          const isElectrodeError =
            recoveryAction === 'check_electrode' ||
            errorMsg.toLowerCase().includes('electrode');

          if (isElectrodeError) {
            setBlockingError({
              title: 'Electrode Not Detected',
              message: 'The measurement electrode was not detected. Please ensure the electrode strip is firmly inserted and try again.',
              canRetry: true,
            });
          } else {
            setErrorMessage(errorMsg || 'The device encountered an error during baseline calibration.');
          }
        }
      }
    };

    startBaseline();

    // No cleanup cancel here — the baseline measurement is intentionally designed to
    // keep running in the background even after the user advances to Step 4.
    // The wizard store receives the result regardless of whether this component is mounted.
    // Device disconnection is now handled by MeasurementInterruptedModal.
  }, [setBaselineProgress, completeBaseline, setBaselineResult, deviceService, testId, selectedProfile, isConnected, setMeasurementInFlight, clearMeasurementInFlight, deviceUid]);

  const handleInterruptedResume = (outcome: 'harvested' | 'still-running') => {
    if (outcome === 'harvested') {
      // Modal has already saved the result; mark baseline done
      baselineStartedRef.current = true;
      setIsBaselineRunning(false);
    } else {
      // Case B: device still running — re-subscribe to receive measurement completion
      setShowRefreshRecovery(false);
      subscribeToRunningBaseline();
    }
  };

  const handleInterruptedDiscard = () => {
    // Modal has already called cancelTest + clearMeasurementInFlight + resetWizard
    // Use SPA navigation to avoid triggering the beforeunload guard and full page reload
    navigate('/');
  };

  // ── In-session baseline disconnect — DeviceReconnectDialog + silent recovery ─
  const {
    disconnected: baselineDisconnected,
    reconnecting: baselineReconnecting,
    reconnectError: baselineReconnectError,
    handleReconnect: handleBaselineReconnect,
    handleCancel: handleBaselineCancel,
  } = usePhaseDisconnectRecovery({
    isActive: isBaselineRunning,
    isConnected,
    showRefreshRecovery,
    onReconnected: async () => {
      try {
        const state = await deviceService.getState();
        const processingId = state?.state?.processingId ?? 0;

        if (processingId > 0) {
          // Device still measuring — subscribe to receive completion
          subscribeToRunningBaseline();
          return;
        }

        // Device idle — check whether result was saved before disconnect
        const currentTestId = useMeasurementWizardStore.getState().testId;
        if (currentTestId) {
          const detail = await testService.getTestById(currentTestId);
          if (detail.baselineCompletedAt) {
            const completedAt = new Date(detail.baselineCompletedAt);
            const deviceInfo = useDeviceStore.getState().deviceInfo;
            const nowDate = new Date();
            const fakeResult = {
              deviceUid: deviceInfo?.uid ?? '',
              deviceSerialNumber: parseInt(deviceInfo?.serialNumber ?? '0') || 0,
              firmwareVersion: deviceInfo?.firmwareVersion ?? '',
              hardwareVersion: deviceInfo?.hardwareVersion ?? '',
              durationMs: 0,
              impedanceResult: {} as any,
              completedAt: nowDate,
            };
            setBaselineResult(fakeResult as any);
            completeBaseline(completedAt);
            setMeasurementInFlight({ phase: 'baseline-complete', deviceUid: deviceInfo?.uid ?? '' });
            if (isMountedRef.current) setIsBaselineRunning(false);
            return;
          }
        }

        // Data lost — throw so the hook shows the error inside the reconnect dialog
        throw new Error(
          'The baseline measurement data could not be recovered. Please cancel and restart the test.',
        );
      } catch (err) {
        logger.warn('[Step3] Silent baseline recovery probe failed:', err);
        // Re-throw so usePhaseDisconnectRecovery surfaces it as reconnectError
        throw err instanceof Error
          ? err
          : new Error('Could not check device status. Try reconnecting the reader.');
      }
    },
    onCancel: async () => navigate('/'),
  });

  return (
    <>
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 0 }}>
        <Typography variant="h5" gutterBottom align="center">
          Upper Respiratory Infection Panel
        </Typography>

        <Box sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          {/* Loop the specimen video 5 times to cover ~60s stirring guidance */}
        <VideoPlayer
            src="/videos/step-3-take-specimen.mp4"
            poster="/videos/step-3-poster.jpg"
            controls
            autoPlay
            loopCount={5}
          />
        </Box>

        <Typography variant="body1" sx={{ mb: 2 }}>
          Open the swab from its packaging and carefully insert 
          into one side of the nose. Rotate the swab 5 times.
        </Typography>

        <Typography variant="body1" sx={{ mb: 4 }}>
          Remove and repeat on the other side of the nose.
        </Typography>

        {/* Button styling: Grey while baseline running, Blue when complete */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => nextStep(4)}
          sx={{
            backgroundColor: (isBaselineRunning) ? '#D3D3D3' : '#1976d2',
            color: (isBaselineRunning) ? '#666666' : '#ffffff',
            '&:hover': {
              backgroundColor: (isBaselineRunning) ? '#D3D3D3' : '#1565c0',
            },
          }}
        >
          Next
        </Button>

        {/* Baseline review — Admin/Researcher only, visible after baseline completes */}
        {!isBaselineRunning &&
          baselineCompletedAt !== null &&
          baselineResult !== null &&
          features.enableResearchFeatures &&
          (user?.roles?.includes('researcher') || user?.roles?.includes('admin')) && (
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => setShowReview(true)}
              sx={{ mt: 1 }}
            >
              Review Baseline
            </Button>
          )}
      </Box>

      {/* Baseline Review full-screen overlay */}
      {features.enableResearchFeatures && baselineResult !== null && baselineCompletedAt !== null && (
        <BaselineReviewOverlay
          open={showReview}
          testId={testId}
          sampleCode={sampleCode}
          baselineResult={baselineResult}
          baselineCompletedAt={baselineCompletedAt}
          onProceed={() => setShowReview(false)}
          onDiscard={() => setShowReview(false)}
        />
      )}

      {/* Blocking Error Dialog (device errors unrelated to disconnect) */}
      <Dialog
        open={blockingError !== null}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{blockingError?.title}</DialogTitle>
        <DialogContent>
          <Alert severity="error">{blockingError?.message}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { navigate('/'); }} variant="outlined">
            Close Wizard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generic Error Modal */}
      <Dialog open={errorMessage !== null} onClose={() => { navigate('/'); }}>
        <DialogTitle>Measurement Error</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {errorMessage || 'The device encountered an error during baseline calibration. Please try again.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { navigate('/'); }} variant="contained">
            Close Wizard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refresh Recovery Modal — stale measurementInFlight detected on mount */}
      {isBaselineRunning && showRefreshRecovery && (
        <MeasurementInterruptedModal
          phase="baseline"
          onResume={handleInterruptedResume}
          onDiscard={handleInterruptedDiscard}
        />
      )}

      {/* In-session Disconnect Recovery — DeviceReconnectDialog with silent auto-resume */}
      {baselineDisconnected && isBaselineRunning && !showRefreshRecovery && (
        <DeviceReconnectDialog
          open
          message="The reader was disconnected during baseline calibration. Reconnect it to continue — the calibration will resume automatically."
          reconnecting={baselineReconnecting}
          reconnectError={baselineReconnectError}
          onReconnect={handleBaselineReconnect}
          onCancelTest={handleBaselineCancel}
        />
      )}
    </>
  );
}
